import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * Reads the last N days of `daily_vitals` + `sleep_record` for the signed-in
 * user, joined by date. The Today vitals strip uses the most recent row plus
 * a 7-day spark; the detail drawer uses the full N-day series.
 *
 * Defaults to 30 days; pass a smaller number for the strip itself.
 */
export type VitalsRow = {
  date: string;
  hrv_status_ms: number | null;
  rhr_bpm: number | null;
  steps: number | null;
  calories_active: number | null;
  // From sleep_record on the same date (joined client-side for simplicity).
  sleep_min: number | null;
  // sources for transparency
  vitals_source: string | null;
  sleep_source: string | null;
};

export function useDailyVitals(lookbackDays = 30) {
  const { session } = useSession();
  const userId = session?.user?.id;

  return useQuery<VitalsRow[]>({
    queryKey: ["daily_vitals", userId, lookbackDays],
    enabled: Boolean(userId),
    // BW12.5 Issue 16: HAE pushes hourly. Stale-while-revalidate every 30s,
    // re-poll every minute while the tab is focused, refresh on focus/reconnect.
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    queryFn: async () => {
      if (!userId) return [];
      const since = new Date();
      since.setDate(since.getDate() - lookbackDays);
      const sinceStr = since.toISOString().slice(0, 10);

      const [vitalsRes, sleepRes] = await Promise.all([
        supabase
          .from("daily_vitals")
          .select("date, hrv_status_ms, rhr_bpm, steps, calories_active, sensor_source")
          .eq("user_id", userId)
          .gte("date", sinceStr)
          .order("date", { ascending: false }),
        supabase
          .from("sleep_record")
          .select("date, duration_min, sensor_source")
          .eq("user_id", userId)
          .gte("date", sinceStr)
          .order("date", { ascending: false }),
      ]);
      if (vitalsRes.error) throw vitalsRes.error;
      if (sleepRes.error) throw sleepRes.error;

      const sleepByDate = new Map<
        string,
        { duration_min: number | null; sensor_source: string | null }
      >();
      for (const s of sleepRes.data ?? []) {
        sleepByDate.set(s.date, {
          duration_min: s.duration_min ?? null,
          sensor_source: s.sensor_source ?? null,
        });
      }

      // Union dates from both sources so we render days where only sleep was
      // synced (or only daily vitals).
      const dates = new Set<string>();
      for (const v of vitalsRes.data ?? []) dates.add(v.date);
      for (const s of sleepRes.data ?? []) dates.add(s.date);

      const vitalsByDate = new Map<string, (typeof vitalsRes.data)[number]>();
      for (const v of vitalsRes.data ?? []) vitalsByDate.set(v.date, v);

      return [...dates]
        .sort((a, b) => (a < b ? 1 : -1))
        .map((date) => {
          const v = vitalsByDate.get(date);
          const s = sleepByDate.get(date);
          return {
            date,
            hrv_status_ms: v?.hrv_status_ms != null ? Number(v.hrv_status_ms) : null,
            rhr_bpm: v?.rhr_bpm ?? null,
            steps: v?.steps ?? null,
            calories_active: v?.calories_active ?? null,
            sleep_min: s?.duration_min ?? null,
            vitals_source: v?.sensor_source ?? null,
            sleep_source: s?.sensor_source ?? null,
          };
        });
    },
  });
}
